import React, { useState, useMemo } from 'react';
import Header from '../components/layout/Header';
import ErrorBanner from '../components/ui/ErrorBanner';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import RevenueChart from '../components/charts/RevenueChart';
import KPICard from '../components/ui/KPICard';
import { useRevenue } from '../hooks/useRevenue';
import { useJobs } from '../hooks/useJobs';
import { formatCurrency, formatDate, truncate } from '../utils/formatters';
import { useBudget } from '../hooks/useForecast';
import { useBuFilter } from '../hooks/useBuMapping';
import DateRangePicker from '../components/ui/DateRangePicker';

function SortHeader({ label, sortKey, sortState, onSort, style }) {
  const active = sortState.key === sortKey;
  const arrow = active ? (sortState.dir === 'asc' ? ' \u25B2' : ' \u25BC') : '';
  return (
    <th style={{ ...style, cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort(sortKey)}>
      {label}{arrow}
    </th>
  );
}

export default function Revenue() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { data, revenueInfo, loading, error } = useRevenue(startDate, endDate);
  const { data: jobsData, loading: jobsLoading } = useJobs(startDate, endDate);
  const { budget } = useBudget(new Date().getFullYear());
  const { filterInvoice, filterJob, units } = useBuFilter();
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  const [selectedBuId, setSelectedBuId] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // Build BU name lookup from ST BU ID
  const buNameMap = useMemo(() => {
    const m = {};
    for (const u of units) {
      if (u.st_business_unit_id) m[u.st_business_unit_id] = u.name;
    }
    return m;
  }, [units]);

  // Revenue data (ST only, no GHL)
  const stRevenue = useMemo(() => {
    return data
      .filter(inv => inv.source === 'servicetitan')
      .filter(filterInvoice)
      .filter(inv => {
        if (selectedBuId && String(inv.businessUnitId) !== String(selectedBuId)) return false;
        if (selectedStatus && inv.status !== selectedStatus) return false;
        return true;
      });
  }, [data, filterInvoice, selectedBuId, selectedStatus]);
  const totalRevenue = stRevenue.reduce((s, inv) => s + (inv.total || inv.amount || 0), 0);

  // Scheduled jobs
  const filteredJobs = useMemo(() => jobsData.filter(filterJob), [jobsData, filterJob]);
  const scheduledJobs = filteredJobs.filter(j => j.status === 'Scheduled' || j.jobStatus === 'Scheduled').length;

  // Sorting
  const handleSort = (key) => {
    setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const sorted = useMemo(() => {
    const arr = [...stRevenue];
    arr.sort((a, b) => {
      let va, vb;
      switch (sort.key) {
        case 'number': va = a.invoiceNumber || a.referenceNumber || ''; vb = b.invoiceNumber || b.referenceNumber || ''; break;
        case 'title': va = a.title || a.name || a.summary || ''; vb = b.title || b.name || b.summary || ''; break;
        case 'amount': va = a.total || a.amount || 0; vb = b.total || b.amount || 0; break;
        case 'bu': va = buNameMap[a.businessUnitId] || ''; vb = buNameMap[b.businessUnitId] || ''; break;
        case 'status': va = a.status || a.reviewStatus || ''; vb = b.status || b.reviewStatus || ''; break;
        case 'date': va = a.effectiveDate || a.invoiceDate || a.createdOn || ''; vb = b.effectiveDate || b.invoiceDate || b.createdOn || ''; break;
        default: va = ''; vb = '';
      }
      if (typeof va === 'number') return sort.dir === 'asc' ? va - vb : vb - va;
      return sort.dir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return arr;
  }, [stRevenue, sort, buNameMap]);

  return (
    <div>
      <Header title="Revenue" />
      <div style={styles.content}>
        <ErrorBanner message={error} />

        <div style={styles.filterRow}>
          <DateRangePicker onChange={({ start, end }) => { setStartDate(start); setEndDate(end); }} defaultPresetIdx={1} />
          
          <div style={styles.localFilters}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Business Unit</label>
              <select 
                value={selectedBuId} 
                onChange={(e) => setSelectedBuId(e.target.value)}
                style={styles.select}
              >
                <option value="">All Business Units</option>
                {units.map(u => (
                  <option key={u.id} value={u.st_business_unit_id}>{u.name}</option>
                ))}
              </select>
            </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Status</label>
              <select 
                value={selectedStatus} 
                onChange={(e) => setSelectedStatus(e.target.value)}
                style={styles.select}
              >
                <option value="">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Posted">Posted</option>
                <option value="Exported">Exported</option>
                <option value="Cleared">Cleared</option>
              </select>
            </div>
          </div>
        </div>

        {budget.revenue_budget > 0 && (
          <div style={styles.budgetBar}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>Annual Revenue Budget</span>
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{formatCurrency(totalRevenue)} / {formatCurrency(budget.revenue_budget)} ({Math.round(totalRevenue / budget.revenue_budget * 100)}%)</span>
            </div>
            <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '999px', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: '999px', transition: 'width 0.4s', width: `${Math.min(100, Math.round(totalRevenue / budget.revenue_budget * 100))}%`, background: totalRevenue >= budget.revenue_budget ? '#22c55e' : '#6366f1' }} />
            </div>
          </div>
        )}

        <div style={styles.kpiRow}>
          <KPICard title="Total Revenue" value={formatCurrency(revenueInfo?.grandTotal || 0)} icon="💵" color="#0f172a" loading={loading} subtitle="Project + Membership" />
          <KPICard title="Job Revenue" value={formatCurrency(revenueInfo?.breakdown?.jobRevenue || 0)} icon="🔧" color="#22c55e" loading={loading} subtitle="Completed job invoices" />
          <KPICard title="Non-Job Revenue" value={formatCurrency(revenueInfo?.breakdown?.nonJobRevenue || 0)} icon="📋" color="#0ea5e9" loading={loading} subtitle="Standalone invoices" />
          <KPICard title="Membership Revenue" value={formatCurrency(revenueInfo?.membershipRevenue || 0)} icon="🎗️" color="#6366f1" loading={loading} subtitle="Gold Plan & Fees" />
        </div>

        <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: '0.5rem', padding: '0.6rem 1rem', fontSize: '0.78rem', color: '#854d0e' }}>
          Figures shown are pre-tax subtotals from completed job invoices and standalone invoices, calculated using ServiceTitan's revenue definition (completed revenue by job completion date, non-job/adjustment revenue by invoice date). Numbers may vary slightly from ServiceTitan's dashboard due to GL account classification at the line-item level.
        </div>

        {revenueInfo?.breakdown && (
          <div style={{ background: '#fff', borderRadius: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '1rem', marginBottom: '1rem' }}>

            {/* Job status breakdown */}
            {revenueInfo.breakdown.byJobStatus?.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h3 style={{ ...styles.sectionTitle, marginBottom: '0.5rem' }}>Job Invoice Breakdown by Status</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9' }}>
                      <th style={{ textAlign: 'left', padding: '0.4rem 0.75rem' }}>Job Status</th>
                      <th style={{ textAlign: 'right', padding: '0.4rem 0.75rem' }}>Invoices</th>
                      <th style={{ textAlign: 'right', padding: '0.4rem 0.75rem' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenueInfo.breakdown.byJobStatus.map((s, i) => (
                      <tr key={s.status} style={{ borderTop: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                        <td style={{ padding: '0.35rem 0.75rem' }}>{s.status}</td>
                        <td style={{ textAlign: 'right', padding: '0.35rem 0.75rem', color: '#64748b' }}>{s.count}</td>
                        <td style={{ textAlign: 'right', padding: '0.35rem 0.75rem', fontWeight: 600 }}>{formatCurrency(s.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* BU breakdown table */}
            <h3 style={{ ...styles.sectionTitle, marginBottom: '0.5rem' }}>Revenue by Business Unit</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.75rem' }}>Business Unit</th>
                  <th style={{ textAlign: 'right', padding: '0.4rem 0.75rem' }}>Invoices</th>
                  <th style={{ textAlign: 'right', padding: '0.4rem 0.75rem' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {(revenueInfo.breakdown.byBU || []).map((bu, i) => (
                  <tr key={bu.name} style={{ borderTop: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td style={{ padding: '0.35rem 0.75rem' }}>{bu.name}</td>
                    <td style={{ textAlign: 'right', padding: '0.35rem 0.75rem', color: '#64748b' }}>{bu.count}</td>
                    <td style={{ textAlign: 'right', padding: '0.35rem 0.75rem', fontWeight: 600 }}>{formatCurrency(bu.total)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid #e2e8f0', fontWeight: 700 }}>
                  <td style={{ padding: '0.4rem 0.75rem' }}>TOTAL</td>
                  <td style={{ textAlign: 'right', padding: '0.4rem 0.75rem' }}>{(revenueInfo.breakdown.byBU || []).reduce((s, b) => s + b.count, 0)}</td>
                  <td style={{ textAlign: 'right', padding: '0.4rem 0.75rem' }}>{formatCurrency((revenueInfo.breakdown.byBU || []).reduce((s, b) => s + b.total, 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div style={styles.chartCard}>
          <h3 style={styles.sectionTitle}>Revenue Trend (by Month)</h3>
          <RevenueChart data={stRevenue} />
        </div>

        <div style={styles.tableCard}>
          <h3 style={styles.sectionTitle}>Invoices ({sorted.length}) — {formatCurrency(totalRevenue)}</h3>
          {loading ? <LoadingSpinner /> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead><tr>
                  <SortHeader label="Invoice #" sortKey="number" sortState={sort} onSort={handleSort} style={styles.th} />
                  <SortHeader label="Title" sortKey="title" sortState={sort} onSort={handleSort} style={styles.th} />
                  <SortHeader label="Amount" sortKey="amount" sortState={sort} onSort={handleSort} style={styles.th} />
                  <SortHeader label="Business Unit" sortKey="bu" sortState={sort} onSort={handleSort} style={styles.th} />
                  <SortHeader label="Status" sortKey="status" sortState={sort} onSort={handleSort} style={styles.th} />
                  <SortHeader label="Date" sortKey="date" sortState={sort} onSort={handleSort} style={styles.th} />
                </tr></thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr><td colSpan={6} style={styles.empty}>No invoices found</td></tr>
                  ) : sorted.map((inv, i) => (
                    <tr key={inv.id || i} style={i % 2 === 0 ? {} : { background: '#f8fafc' }}>
                      <td style={styles.td}>{truncate(inv.invoiceNumber || inv.referenceNumber || inv.id || '\u2014', 16)}</td>
                      <td style={styles.td}>{truncate(inv.title || inv.name || inv.summary || '\u2014', 30)}</td>
                      <td style={{ ...styles.td, fontWeight: '600' }}>{formatCurrency(inv.total || inv.amount || 0)}</td>
                      <td style={styles.td}><span style={styles.buTag}>{buNameMap[inv.businessUnitId] || '\u2014'}</span></td>
                      <td style={styles.td}>{inv.status || inv.reviewStatus || '\u2014'}</td>
                      <td style={styles.td}>{formatDate(inv.effectiveDate || inv.invoiceDate || inv.createdOn)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  content: { padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
  budgetBar: { background: '#fff', borderRadius: '0.75rem', padding: '0.75rem 1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e0e7ff' },
  kpiRow: { display: 'flex', gap: '1rem', flexWrap: 'wrap' },
  chartCard: { background: '#fff', borderRadius: '0.75rem', padding: '1.25rem 1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  tableCard: { background: '#fff', borderRadius: '0.75rem', padding: '1.25rem 1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  sectionTitle: { margin: '0 0 1rem 0', fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  th: { padding: '0.625rem 0.75rem', background: '#f8fafc', color: '#64748b', fontWeight: '600', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.625rem 0.75rem', borderBottom: '1px solid #f1f5f9', color: '#1e293b', verticalAlign: 'middle' },
  buTag: { padding: '0.15rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '500', background: '#ede9fe', color: '#6366f1' },
  empty: { padding: '2rem', textAlign: 'center', color: '#94a3b8' },
  filterRow: { display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', background: '#fff', padding: '1rem', borderRadius: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  localFilters: { display: 'flex', gap: '1rem', marginLeft: 'auto' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  filterLabel: { fontSize: '0.7rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' },
  select: { padding: '0.4rem 0.6rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', fontSize: '0.8rem', color: '#374151', background: '#f8fafc', outline: 'none', minWidth: '160px' },
};
